import { createFileRoute } from "@tanstack/react-router";
import { Container, Heading, Text, VStack, Box, Table } from "@chakra-ui/react";
import { Logo } from "@/components/logo";

export const Route = createFileRoute("/privacy")({
  component: Privacy,
});

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <VStack align="start" gap="3" w="100%">
      <Heading as="h2" size="lg" fontWeight="semibold">
        {title}
      </Heading>
      {children}
    </VStack>
  );
}

function Subsection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <VStack align="start" gap="2" w="100%">
      <Heading as="h3" size="md" fontWeight="medium">
        {title}
      </Heading>
      {children}
    </VStack>
  );
}

function Privacy() {
  return (
    <Box minH="100vh" py={{ base: "12", md: "16" }} px={{ base: "6", md: "0" }}>
      <Container maxW="2xl">
        <VStack align="start" gap="8">
          <Logo />

          <VStack align="start" gap="2">
            <Heading as="h1" size={{ base: "3xl", md: "4xl" }}>
              Privacy Policy
            </Heading>
            <Text color="fg.muted" fontSize="sm">
              Last updated: February 21, 2026
            </Text>
          </VStack>

          <Section title="Overview">
            <Text>
              Loonie is an expense-sharing application operated by Cole MacKenzie ("I", "me", or
              "my"). This policy describes what personal data I collect, how I use it, and your
              rights.
            </Text>
          </Section>

          <Section title="Data I Collect">
            <Subsection title="Account data">
              <Text>
                When you sign in with Google, I receive your name, email address, and a unique
                identifier from Google (the "sub" claim). This data is stored in Loonie's database
                to identify your account.
              </Text>
            </Subsection>
            <Subsection title="App data">
              <Text>As you use Loonie, I store:</Text>
              <Box as="ul" pl="5" mt="1">
                <Box as="li">
                  <Text>Expenses: description, amount, currency, date, and split details</Text>
                </Box>
                <Box as="li">
                  <Text>Settlement records between you and friends</Text>
                </Box>
                <Box as="li">
                  <Text>Friend relationships (pending and accepted)</Text>
                </Box>
                <Box as="li">
                  <Text>Your preferred default currency</Text>
                </Box>
                <Box as="li">
                  <Text>An activity log of expense and settlement events</Text>
                </Box>
              </Box>
            </Subsection>
            <Subsection title="Cookies">
              <Text>
                A session cookie (<Box as="code">__session</Box>) is stored in your browser for up
                to 7 days to keep you signed in. It is HMAC-signed and scoped to this application.
                No third-party tracking cookies are used.
              </Text>
            </Subsection>
            <Subsection title="Emails">
              <Text>
                I send transactional emails — expense notifications and daily summaries — via
                Resend. These include your name, email address, and expense details relevant to you.
              </Text>
            </Subsection>
          </Section>

          <Section title="How I Use Your Data">
            <Text>Your data is used solely to operate Loonie:</Text>
            <Box as="ul" pl="5" mt="1">
              <Box as="li">
                <Text>Authenticate your identity</Text>
              </Box>
              <Box as="li">
                <Text>Display your expenses, balances, and friends</Text>
              </Box>
              <Box as="li">
                <Text>Compute what you owe or are owed</Text>
              </Box>
              <Box as="li">
                <Text>Send you expense notifications</Text>
              </Box>
            </Box>
            <Text>
              I do not sell your data, use it for advertising, or share it with third parties except
              as described below.
            </Text>
          </Section>

          <Section title="Third-Party Services">
            <Text>The following services process your data as part of operating Loonie:</Text>
            <Table.Root variant="outline" size="sm">
              <Table.Header>
                <Table.Row>
                  <Table.ColumnHeader>Service</Table.ColumnHeader>
                  <Table.ColumnHeader>Purpose</Table.ColumnHeader>
                  <Table.ColumnHeader>Data shared</Table.ColumnHeader>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                <Table.Row>
                  <Table.Cell>Google OAuth</Table.Cell>
                  <Table.Cell>Authentication</Table.Cell>
                  <Table.Cell>
                    You authenticate directly with Google; I receive your name, email, and Google ID
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Cloudflare</Table.Cell>
                  <Table.Cell>App hosting and runtime</Table.Cell>
                  <Table.Cell>All app traffic passes through Cloudflare's network</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>PlanetScale</Table.Cell>
                  <Table.Cell>Database storage</Table.Cell>
                  <Table.Cell>All app data, stored with full disk encryption</Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>Resend</Table.Cell>
                  <Table.Cell>Transactional email</Table.Cell>
                  <Table.Cell>Your name, email, and relevant expense details</Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table.Root>
          </Section>

          <Section title="Data Retention">
            <Text>
              I retain your data for as long as your account is active. You may request deletion at
              any time by emailing{" "}
              <Box as="a" href="mailto:privacy@loonie.app" color="loonie.fg">
                privacy@loonie.app
              </Box>
              . Upon request, I will delete your account and associated personal data.
            </Text>
          </Section>

          <Section title="Your Rights — EU/EEA (GDPR)">
            <Text>
              If you are located in the EU or EEA, you have the following rights under the GDPR:
            </Text>
            <Box as="ul" pl="5" mt="1">
              <Box as="li">
                <Text>
                  <Box as="strong">Access</Box> — Request a copy of your personal data
                </Text>
              </Box>
              <Box as="li">
                <Text>
                  <Box as="strong">Correction</Box> — Request correction of inaccurate data
                </Text>
              </Box>
              <Box as="li">
                <Text>
                  <Box as="strong">Deletion</Box> — Request erasure of your data ("right to be
                  forgotten")
                </Text>
              </Box>
              <Box as="li">
                <Text>
                  <Box as="strong">Portability</Box> — Receive your data in a portable format
                </Text>
              </Box>
              <Box as="li">
                <Text>
                  <Box as="strong">Objection</Box> — Object to processing of your personal data
                </Text>
              </Box>
            </Box>
            <Text>
              To exercise these rights, email{" "}
              <Box as="a" href="mailto:privacy@loonie.app" color="loonie.fg">
                privacy@loonie.app
              </Box>
              .
            </Text>
          </Section>

          <Section title="Your Rights — California (CCPA)">
            <Text>
              If you are a California resident, you have the following rights under the CCPA:
            </Text>
            <Box as="ul" pl="5" mt="1">
              <Box as="li">
                <Text>
                  <Box as="strong">Know</Box> — Request disclosure of what personal information is
                  collected about you
                </Text>
              </Box>
              <Box as="li">
                <Text>
                  <Box as="strong">Delete</Box> — Request deletion of your personal information
                </Text>
              </Box>
              <Box as="li">
                <Text>
                  <Box as="strong">Opt-out of sale</Box> — I do not sell personal information
                </Text>
              </Box>
              <Box as="li">
                <Text>
                  <Box as="strong">Non-discrimination</Box> — I will not discriminate against you
                  for exercising your rights
                </Text>
              </Box>
            </Box>
            <Text>
              To exercise these rights, email{" "}
              <Box as="a" href="mailto:privacy@loonie.app" color="loonie.fg">
                privacy@loonie.app
              </Box>
              .
            </Text>
          </Section>

          <Section title="Security">
            <Text>
              All data is stored in an encrypted database with full disk encryption. Session cookies
              are HMAC-signed and scoped to this application. All communications are encrypted in
              transit via HTTPS.
            </Text>
          </Section>

          <Section title="Children">
            <Text>
              Loonie is not directed at children under 13. I do not knowingly collect personal data
              from children. If you believe a child's data has been collected, please contact me at{" "}
              <Box as="a" href="mailto:privacy@loonie.app" color="loonie.fg">
                privacy@loonie.app
              </Box>
              .
            </Text>
          </Section>

          <Section title="Changes to This Policy">
            <Text>
              If I make material changes to this policy, I will update the "Last updated" date at
              the top. Continued use of Loonie after changes constitutes acceptance of the updated
              policy.
            </Text>
          </Section>

          <Section title="Contact">
            <Text>
              For privacy questions, data requests, or concerns, email{" "}
              <Box as="a" href="mailto:privacy@loonie.app" color="loonie.fg">
                privacy@loonie.app
              </Box>
              .
            </Text>
          </Section>

          <Box pt="4" borderTopWidth="1px" borderColor="border" w="100%">
            <Box as="a" href="/" color="loonie.fg" fontSize="sm">
              ← Back to Loonie
            </Box>
          </Box>
        </VStack>
      </Container>
    </Box>
  );
}
